import { type JSX, Fragment, useCallback, useMemo } from 'react';
import { type ChainflipAsset, assetConstants } from '@chainflip/utils/chainflip';
import { UTCDate } from '@date-fns/utc';
import classNames from 'classnames';
import { subDays } from 'date-fns';
import { ChartLegendRectangle } from '@/shared/components/ChartLegendRectangle';
import { BarChart, type BarDatumRecord } from '@/shared/components/charts/BarChart';
import { Divider } from '@/shared/components/Divider';
import { ChainLogo } from '@/shared/components/logo/ChainLogo';
import { ChartLegend } from '@/shared/components/molecules/ChartLegend';
import {
  type GetLpSupplyPnlQuery,
  type LpLendingPoolSnapshotGroupBy,
} from '@/shared/graphql/generated/lp/graphql';
import { getLpSupplyPnl } from '@/shared/graphql/lp/lending';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import TrendingDown from '@/shared/icons/flip-ui-kit/small/TrendingDown';
import TrendingUp from '@/shared/icons/flip-ui-kit/small/TrendingUp';
import { assetColors } from '@/shared/utils/chainflip';
import { getDateAxisTicks } from '@/shared/utils/charts';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import { SkeletonLine } from '../atoms/LoadingSkeleton';
import { FancyDollarDisplay } from '../FancyDollarDisplay';
import QuestionMarkTooltip from '../QuestionMarkTooltip';

const LegendItem = ({ asset }: { asset: ChainflipAsset }) => (
  <div className="flex items-center gap-x-1 font-aeonikMedium text-12 text-cf-light-3">
    <ChainLogo chain={assetConstants[asset].chain} variant="monochromatic" />
    <span>{chainflipAssetMap[asset].symbol}</span>
  </div>
);

const BarTooltip = ({
  datum,
  assets,
  isDaily,
}: {
  datum: BarDatumRecord;
  assets: ChainflipAsset[];
  isDaily: boolean;
}): JSX.Element => {
  const date = new UTCDate(datum.date as string);
  const totalUsd = Number(datum.totalUsd ?? 0);
  return (
    <div className="w-[275px]">
      <div
        className={classNames(
          'flex flex-col items-start font-aeonikMedium',
          assets.length > 1 ? 'pb-3' : 'pb-0',
        )}
      >
        <div className="text-12 text-cf-light-2">
          <div>
            {isDaily
              ? date.toLocaleString(undefined, { timeZone: 'UTC' })
              : date.toLocaleDateString(undefined, { timeZone: 'UTC' })}{' '}
            UTC
          </div>
          <div
            className={classNames(
              'text-20',
              // eslint-disable-next-line no-nested-ternary
              totalUsd === 0 ? 'text-cf-white' : totalUsd > 0 ? 'text-cf-green-3' : 'text-cf-red-1',
            )}
          >
            {formatUsdValue(totalUsd, undefined, true)}
          </div>
        </div>
      </div>
      {assets.length > 1 && (
        <>
          <Divider className="h-px" />
          <div className="flex flex-col gap-y-2.5 pt-3 text-12 leading-6">
            {assets.map((asset) => {
              const usdValue = Number(datum[`${asset}-usd`] ?? 0);
              const rawAmount = datum[asset] ?? '0';
              const amount = TokenAmount.fromAsset(String(rawAmount), asset);
              return (
                <Fragment key={asset}>
                  <div className="flex flex-row items-center justify-between gap-x-2 font-aeonikMedium">
                    <div className="flex items-center gap-x-1">
                      <ChartLegendRectangle fill={assetColors[asset]} />
                      <LegendItem asset={asset} />
                    </div>
                    <div className="flex flex-row gap-x-1">
                      <span
                        className={classNames(
                          'text-12',
                          // eslint-disable-next-line no-nested-ternary
                          amount.eq(0)
                            ? 'text-cf-white'
                            : amount.gt(0)
                              ? 'text-cf-green-3'
                              : 'text-cf-red-1',
                        )}
                      >
                        {amount.gt(0) && '+'}
                        {amount.toFormatted()} {chainflipAssetMap[asset].symbol}
                      </span>
                      <span className="text-12 text-cf-light-2">
                        ({formatUsdValue(usdValue, undefined, true)})
                      </span>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const EarnedSupplyFeesChart = ({ days, idSs58 }: { days: number; idSs58: string }) => {
  const { lendingPools } = useLendingPools();
  const poolAssets = useMemo(
    () => lendingPools.map((pool) => pool.asset as ChainflipAsset),
    [lendingPools],
  );

  const isDaily = days === 1;
  const groupBy: LpLendingPoolSnapshotGroupBy[] = isDaily
    ? ['ASSET', 'TIMESTAMP_HOUR']
    : // TODO: [WEB-3068] Use TIMESTAMP_DAY when available on the backend
      ['ASSET', 'TIMESTAMP_HOUR_TRUNCATED_TO_DAY'];

  const { startDate, endDate, last24hStartDate } = useMemo(
    () => ({
      startDate: subDays(new Date(), days),
      endDate: new Date(),
      last24hStartDate: subDays(new Date(), 1),
    }),
    [days],
  );

  const dataSelector = useCallback(
    (data: GetLpSupplyPnlQuery) => {
      const feesByDateMap = new Map<string, BarDatumRecord>();
      const agg = data.account?.snapshots.agg;

      if (!agg) return [];

      for (const row of agg) {
        const asset = row.keys?.[0] as ChainflipAsset;
        const date = row.keys?.[1] as string;
        if (!feesByDateMap.has(date)) feesByDateMap.set(date, { date, totalUsd: 0 });
        const entry = feesByDateMap.get(date)!;
        entry[`${asset}-usd`] = Number(row.sum?.supplyPnl1HUsd ?? 0);
        entry[asset] = row.sum?.supplyPnl1H ?? '0';
      }

      return Array.from(feesByDateMap.values())
        .map(
          (entry) =>
            ({
              ...entry,
              totalUsd: poolAssets.reduce(
                (sum, asset) => sum + Number(entry[`${asset}-usd`] ?? 0),
                0,
              ),
            }) as BarDatumRecord & { totalUsd: number },
        )
        .sort((a, b) => (a.date as string).localeCompare(b.date as string));
    },
    [poolAssets],
  );

  const { data: chartData, isLoading } = useGqlQuery(getLpSupplyPnl, {
    variables: {
      idSs58,
      groupBy,
      filters: {
        and: [
          { timestamp: { greaterThanOrEqualTo: startDate.toISOString() } },
          { timestamp: { lessThanOrEqualTo: endDate.toISOString() } },
        ],
      },
    },
    refetchInterval: 30_000,
    context: { clientName: 'lpProcessor' },
    select: dataSelector,
  });

  const { data: last24hSnapshotData, isLoading: isLoadingLast24h } = useGqlQuery(getLpSupplyPnl, {
    variables: {
      idSs58,
      groupBy: ['ASSET'],
      filters: {
        and: [
          { timestamp: { greaterThanOrEqualTo: last24hStartDate.toISOString() } },
          { timestamp: { lessThanOrEqualTo: endDate.toISOString() } },
        ],
      },
    },
    refetchInterval: 30_000,
    context: { clientName: 'lpProcessor' },
    select: dataSelector,
  });

  const total24hPnlUsd = useMemo(() => {
    if (!last24hSnapshotData) return 0;
    return last24hSnapshotData.reduce((sum, item) => sum + Number(item?.totalUsd), 0);
  }, [last24hSnapshotData]);

  const totalPnlUsd = useMemo(
    () => chartData?.reduce((sum, entry) => sum + Number(entry?.totalUsd ?? 0), 0) ?? 0,
    [chartData],
  );

  const keys = useMemo(() => poolAssets.map((asset) => `${asset}-usd`), [poolAssets]);
  const barColors = useMemo(() => poolAssets.map((asset) => assetColors[asset]), [poolAssets]);
  const chartDates = useMemo(() => (chartData ?? []).map((d) => d.date as string), [chartData]);

  const xTickFormat = useCallback(
    (dateString: string) => {
      const date = new UTCDate(dateString);
      if (isDaily) {
        return date.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: 'numeric',
          timeZone: 'UTC',
        });
      }
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    },
    [isDaily],
  );
  const xTickValues = useMemo(() => getDateAxisTicks(chartDates, 10), [chartDates]);

  const yTickFormat = useCallback(
    (value: string | number): string => String(formatUsdValue(value, false)),
    [],
  );

  const renderTooltip = useCallback(
    (datum: BarDatumRecord) => <BarTooltip datum={datum} assets={poolAssets} isDaily={isDaily} />,
    [poolAssets, isDaily],
  );

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2">
          {isLoading ? (
            <SkeletonLine width={100} height={32} />
          ) : (
            <FancyDollarDisplay
              value={formatUsdValue(totalPnlUsd, undefined, true)}
              wholeClassName="text-24"
              decimalClassName="text-18"
            />
          )}
          <div className="flex gap-1 text-12">
            <div className="flex items-center justify-center">
              {isLoadingLast24h || isLoading ? (
                <SkeletonLine width={45} height={12} />
              ) : (
                <>
                  {total24hPnlUsd < 0 ? (
                    <TrendingDown className="text-cf-red-2" />
                  ) : (
                    <TrendingUp className="text-cf-green-3" />
                  )}
                  <span className={total24hPnlUsd < 0 ? 'text-cf-red-2' : 'text-cf-green-3'}>
                    {total24hPnlUsd > 0 && '+'}
                    {formatUsdValue(total24hPnlUsd, undefined, true)}
                  </span>
                </>
              )}
            </div>
            <span className="text-cf-light-2">Last 24H</span>
            <QuestionMarkTooltip content="Yield earned or lost during the last 24 hours." />
          </div>
        </div>
        <div className="flex flex-col">
          <BarChart
            chartId={`earned-supply-fees-${idSs58}`}
            className="-mx-4 h-[290px]"
            isLoading={isLoading}
            data={chartData ?? []}
            keys={keys}
            barColors={barColors}
            chartDates={chartDates}
            margin={{ top: 20, right: 40, bottom: 50, left: 62 }}
            padding={0.4}
            gridStroke="#424242"
            axisTickColor="#909090"
            xTickFormat={xTickFormat}
            xTickValues={xTickValues}
            yTickFormat={yTickFormat}
            renderTooltip={renderTooltip}
          />
          <ChartLegend
            items={poolAssets.map((asset) => ({
              label: <LegendItem asset={asset} />,
              color: assetColors[asset],
            }))}
          />
        </div>
      </div>
    </div>
  );
};
